import type { ContentType, EstimationParams, ImageSizeTier, ModelInfo } from '@tcc/shared';
import { deriveVolumetrics, type VolumetricsField } from '@tcc/shared';
import {
	CONTENT_TYPES,
	CONTEXT_UNITS,
	IMAGE_SIZES,
	PRICING_TIERS,
	RESPONSE_SIZES,
	STEP_DOTS,
	STEP_SIDEBAR,
	VOL_MAX,
} from '../../constants.js';
import type { WizardState } from '../../state/state.js';

export type Updater = (patch: Partial<WizardState>) => void;

// ---------------------------------------------------------------------------
// Sidebar
// ---------------------------------------------------------------------------

export function WizardSidebar({
	activeStep,
	summaryActive,
	onPick,
}: {
	activeStep: number;
	summaryActive: boolean;
	onPick: (k: number) => void;
}) {
	return (
		<aside className="wsteps" aria-label="Steps">
			<ol>
				{STEP_DOTS.map((k) => {
					const done = summaryActive || k < activeStep;
					const cur = !summaryActive && k === activeStep;
					return (
						<li key={k}>
							<button
								type="button"
								className={`wstep${cur ? ' cur' : done ? ' done' : ''}`}
								onClick={() => onPick(k)}
								aria-current={cur ? 'step' : undefined}
							>
								<span className="wstep-n" aria-hidden="true">
									{done ? '✓' : k + 1}
								</span>
								<span className="wstep-l">{STEP_SIDEBAR[k]}</span>
							</button>
						</li>
					);
				})}
				<li>
					<hr className="wstep-sep" />
					<div
						className={`wstep summary${summaryActive ? ' cur' : ''}`}
						aria-current={summaryActive ? 'step' : undefined}
					>
						<span className="wstep-n">✓</span>
						<span className="wstep-l">Summary</span>
					</div>
				</li>
			</ol>
		</aside>
	);
}

// ---------------------------------------------------------------------------
// Step components
// ---------------------------------------------------------------------------

export function Step1Content({ contentTypes, update }: { contentTypes: ContentType[]; update: Updater }) {
	return (
		<fieldset>
			<legend className="sr-only">Select one content type</legend>
			<div className="optgrid">
				{CONTENT_TYPES.map((ct) => {
					const on = contentTypes.includes(ct.id);
					return (
						<label key={ct.id} className={`optcard${on ? ' on' : ''}`}>
							<input
								type="radio"
								name="contentType"
								value={ct.id}
								checked={on}
								onChange={() => update({ contentTypes: [ct.id] })}
								className="ropt-input"
							/>
							<span className="optcard-body">
								<span className="optcard-head">
									<span className="optcard-title">{ct.label}</span>
									{ct.common && <span className="optcard-tag">Most common</span>}
								</span>
								<span className="optcard-desc">{ct.desc}</span>
							</span>
							<span className="optcard-radio" aria-hidden="true" />
						</label>
					);
				})}
			</div>
		</fieldset>
	);
}

export function Step2Interaction({
	interactionType,
	bulkSize,
	turnsPerConversation,
	liveInput,
	update,
}: {
	interactionType: WizardState['interactionType'];
	bulkSize: number;
	turnsPerConversation: number;
	liveInput: number | null;
	update: Updater;
}) {
	const bulk = interactionType === 'batch' ? Math.max(1, bulkSize) : 1;
	const perReq = liveInput != null ? liveInput * bulk : null;
	const over = perReq != null && perReq > 200_000;

	return (
		<>
			<fieldset>
				<legend className="sr-only">Interaction type</legend>
				<div className="rstack">
					{(
						[
							[
								'assistive',
								'Assistive',
								'A person interacts with the AI in real time (e.g. a chat assistant or drafting aid).',
							],
							[
								'batch',
								'Batch / automated',
								'The AI processes items on its own, without a person in the loop for each one.',
							],
						] as const
					).map(([id, label, desc]) => (
						<label key={id} className={`ropt${interactionType === id ? ' on' : ''}`}>
							<input
								type="radio"
								name="interactionType"
								value={id}
								checked={interactionType === id}
								onChange={() => update({ interactionType: id })}
								className="ropt-input"
							/>
							<div className="rdot" aria-hidden="true" />
							<div>
								<span className="rtxt">{label}</span>
								<span className="rdesc">{desc}</span>
							</div>
						</label>
					))}
				</div>
			</fieldset>
			{interactionType === 'batch' && (
				<div style={{ marginTop: 12 }}>
					<label htmlFor="bulk-size" className="flbl" style={{ display: 'block', marginBottom: 6 }}>
						Inputs per bulk request
					</label>
					<input
						id="bulk-size"
						type="number"
						min={1}
						step={1}
						value={bulkSize}
						onChange={(e) => {
							const v = Number(e.target.value);
							if (Number.isFinite(v)) update({ bulkSize: Math.max(1, Math.trunc(v)) });
						}}
						className="num"
						style={{ width: 120 }}
					/>
					<div className="note">
						How many inputs are grouped into one model request. Enter 1 if each item is processed separately.
					</div>
				</div>
			)}
			{interactionType === 'assistive' && (
				<div style={{ marginTop: 12 }}>
					<label htmlFor="turns-per-conv" className="flbl" style={{ display: 'block', marginBottom: 6 }}>
						Turns per conversation
					</label>
					<input
						id="turns-per-conv"
						type="number"
						min={1}
						step={1}
						value={turnsPerConversation}
						onChange={(e) => {
							const v = Number(e.target.value);
							if (Number.isFinite(v)) update({ turnsPerConversation: Math.max(1, Math.trunc(v)) });
						}}
						className="num"
						style={{ width: 120 }}
					/>
					<div className="note">
						Average number of back-and-forth steps in one conversation. Monthly turns = inputs × this.
					</div>
				</div>
			)}
			{/* Always-visible per-request token hint (yellow), for both assistive and batch. */}
			{perReq != null && perReq > 0 && (
				<div
					className="note"
					style={
						over
							? { marginTop: 12, background: '#fff8e1', borderLeftColor: '#e0a800', color: '#5f4b00' }
							: { marginTop: 12 }
					}
				>
					{over ? '⚠ ' : ''}Each request ≈ {perReq.toLocaleString()} input tokens
					{over ? ' — above 200k, priced at the higher long-context (higher model cost) rate.' : '.'}
				</div>
			)}
		</>
	);
}

export function Step3Inputs({
	volumetrics,
	contentTypes,
	dataUnits,
	params,
	update,
}: {
	volumetrics: WizardState['volumetrics'];
	contentTypes: ContentType[];
	dataUnits: WizardState['dataUnits'];
	params: EstimationParams;
	update: Updater;
}) {
	return (
		<>
			<p className="sub">
				Monthly and yearly are worked out from your daily figure. Change any field to override. The days-per-month and
				days-per-year basis can be adjusted on the Configuration tab.
			</p>
			<div className="vgrid">
				{(['perDay', 'perMonth', 'perYear'] as VolumetricsField[]).map((field, idx) => (
					<div className="vcell" key={field}>
						<div className="vper">{['Per day', 'Per month', 'Per year'][idx]}</div>
						<input
							type="number"
							min={0}
							max={VOL_MAX}
							placeholder="e.g. 100"
							aria-label={['Per day', 'Per month', 'Per year'][idx]}
							value={volumetrics[field] || ''}
							onChange={(e) => {
								const v = Number(e.target.value);
								if (Number.isFinite(v))
									update({
										volumetrics: deriveVolumetrics(
											field,
											Math.max(0, Math.trunc(v)),
											params.daysPerMonth,
											params.daysPerYear,
										),
									});
							}}
						/>
					</div>
				))}
			</div>

			<p style={{ fontWeight: 600, fontSize: 15, color: '#111', margin: '22px 0 4px' }}>How big is a typical input?</p>
			<p className="sub">Roughly one page ≈ 750 tokens.</p>
			{contentTypes.includes('text') && (
				<div className="drow" style={{ gridTemplateColumns: '92px 1fr 132px' }}>
					<div className="dtype">Text</div>
					<input
						type="range"
						aria-label="Text size in pages"
						min={params.sizingRanges.textPages.min}
						max={params.sizingRanges.textPages.max}
						value={dataUnits.textPages}
						onChange={(e) => update({ dataUnits: { ...dataUnits, textPages: Number(e.target.value) } })}
					/>
					<div className="dval" style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
						<input
							type="number"
							aria-label="Text pages"
							className="num"
							min={params.sizingRanges.textPages.min}
							max={params.sizingRanges.textPages.max}
							value={dataUnits.textPages}
							onChange={(e) => {
								const v = Number(e.target.value);
								if (Number.isFinite(v))
									update({
										dataUnits: {
											...dataUnits,
											textPages: Math.min(
												params.sizingRanges.textPages.max,
												Math.max(params.sizingRanges.textPages.min, Math.trunc(v)),
											),
										},
									});
							}}
							style={{ width: 64 }}
						/>
						<span>/ {params.sizingRanges.textPages.max} pages</span>
					</div>
				</div>
			)}
			{contentTypes.includes('voice') && (
				<div className="drow">
					<div className="dtype">Voice</div>
					<input
						type="range"
						aria-label="Voice length in minutes"
						min={params.sizingRanges.voiceMinutes.min}
						max={params.sizingRanges.voiceMinutes.max}
						value={dataUnits.voiceMinutes}
						onChange={(e) => update({ dataUnits: { ...dataUnits, voiceMinutes: Number(e.target.value) } })}
					/>
					<div className="dval">{dataUnits.voiceMinutes} min</div>
				</div>
			)}
			{contentTypes.includes('images') && (
				<div className="drow">
					<div className="dtype">Images</div>
					<div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
						<input
							type="range"
							aria-label="Image count"
							min={params.sizingRanges.imageCount.min}
							max={params.sizingRanges.imageCount.max}
							value={dataUnits.imageCount}
							onChange={(e) => update({ dataUnits: { ...dataUnits, imageCount: Number(e.target.value) } })}
						/>
						<div style={{ display: 'flex', justifyContent: 'flex-end' }}>
							<select
								aria-label="Image size"
								value={dataUnits.imageSize}
								onChange={(e) => update({ dataUnits: { ...dataUnits, imageSize: e.target.value as ImageSizeTier } })}
							>
								{IMAGE_SIZES.map((s) => (
									<option key={s.id} value={s.id}>
										{s.label}
									</option>
								))}
							</select>
						</div>
					</div>
					<div className="dval">{dataUnits.imageCount} imgs</div>
				</div>
			)}
			{contentTypes.includes('video') && (
				<div className="drow">
					<div className="dtype">Video</div>
					<input
						type="range"
						aria-label="Video length in minutes"
						min={params.sizingRanges.videoMinutes.min}
						max={params.sizingRanges.videoMinutes.max}
						value={dataUnits.videoMinutes}
						onChange={(e) => update({ dataUnits: { ...dataUnits, videoMinutes: Number(e.target.value) } })}
					/>
					<div className="dval">{dataUnits.videoMinutes} min</div>
				</div>
			)}
			{contentTypes.length === 0 && <p className="sub">No content types selected.</p>}
		</>
	);
}

export function Step4Context({
	additionalContext,
	update,
}: {
	additionalContext: WizardState['additionalContext'];
	update: Updater;
}) {
	return (
		<>
			<p className="help">
				For example: attachments, reference documents, or background information sent with every request. Most simple
				processes are "No".
			</p>
			<fieldset>
				<legend className="sr-only">Include additional context</legend>
				<div className="r2col">
					{(
						[
							['yes', 'Yes'],
							['no', 'No'],
						] as const
					).map(([id, label]) => (
						<label key={id} className={`ropt${(additionalContext.enabled ? 'yes' : 'no') === id ? ' on' : ''}`}>
							<input
								type="radio"
								name="additionalContext"
								value={id}
								checked={(additionalContext.enabled ? 'yes' : 'no') === id}
								onChange={() => update({ additionalContext: { ...additionalContext, enabled: id === 'yes' } })}
								className="ropt-input"
							/>
							<div className="rdot" aria-hidden="true" />
							<div className="rtxt">{label}</div>
						</label>
					))}
				</div>
			</fieldset>
			{additionalContext.enabled && (
				<div className="branch active">
					<fieldset>
						<legend className="flbl" style={{ marginBottom: 7 }}>
							What size is each piece of additional context?
						</legend>
						<div className="rstack">
							{CONTEXT_UNITS.map((os) => (
								<label key={os.id} className={`ropt${additionalContext.unit === os.id ? ' on' : ''}`}>
									<input
										type="radio"
										name="additionalContextUnit"
										value={os.id}
										checked={additionalContext.unit === os.id}
										onChange={() => update({ additionalContext: { ...additionalContext, unit: os.id } })}
										className="ropt-input"
									/>
									<div className="rdot" aria-hidden="true" />
									<div className="rtxt">{os.label}</div>
								</label>
							))}
						</div>
					</fieldset>
					<label
						style={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							gap: 10,
							marginTop: 10,
						}}
					>
						<span className="flbl" style={{ marginBottom: 0 }}>
							How many?
						</span>
						<input
							type="number"
							min={1}
							max={1000000}
							value={additionalContext.count}
							className="num"
							style={{ width: 100 }}
							onChange={(e) =>
								update({
									additionalContext: {
										...additionalContext,
										count: Math.max(1, Math.trunc(Number(e.target.value) || 1)),
									},
								})
							}
						/>
					</label>
				</div>
			)}
		</>
	);
}

export function Step5Decisions({
	decisionMaking,
	update,
}: {
	decisionMaking: WizardState['decisionMaking'];
	update: Updater;
}) {
	const dm = decisionMaking;
	return (
		<>
			<p className="help">
				"Decisions" are choices the AI makes that affect outcomes, need confidence, or that a human may need to review.
				Low impact = routine or simple; High impact = sensitive or high-stakes.
			</p>
			<fieldset>
				<legend className="sr-only">Decision making required</legend>
				<div className="r2col">
					{(
						[
							['yes', 'Yes'],
							['no', 'No'],
						] as const
					).map(([id, label]) => (
						<label key={id} className={`ropt${dm.choice === id ? ' on' : ''}`}>
							<input
								type="radio"
								name="decisionMaking"
								value={id}
								checked={dm.choice === id}
								onChange={() => update({ decisionMaking: { ...dm, choice: id } })}
								className="ropt-input"
							/>
							<div className="rdot" aria-hidden="true" />
							<div className="rtxt">{label}</div>
						</label>
					))}
				</div>
			</fieldset>
			{dm.choice === 'yes' && (
				<div className="branch active">
					<div className="flbl" style={{ marginBottom: 8 }}>
						How many decisions of each type per input?
					</div>
					{(
						[
							['decisionsSimple', 'Low impact'],
							['decisionsComplex', 'High impact'],
						] as const
					).map(([key, label]) => (
						<label
							key={key}
							style={{
								display: 'flex',
								justifyContent: 'space-between',
								alignItems: 'center',
								gap: 10,
								marginBottom: 8,
							}}
						>
							<span style={{ fontSize: 13, color: '#111' }}>{label}</span>
							<input
								type="number"
								min={0}
								max={1000000}
								value={dm[key]}
								className="num"
								style={{ width: 100 }}
								onChange={(e) =>
									update({ decisionMaking: { ...dm, [key]: Math.max(0, Math.trunc(Number(e.target.value) || 0)) } })
								}
							/>
						</label>
					))}
				</div>
			)}
		</>
	);
}

export function Step6Output({
	outputSize,
	outputSizeCount,
	expert,
	modelId,
	pricingTier,
	cachedInputRatio,
	thinkingTokensPerTurn,
	models,
	update,
}: {
	outputSize: WizardState['outputSize'];
	outputSizeCount: number;
	expert: boolean;
	modelId: string | undefined;
	pricingTier: WizardState['pricingTier'];
	cachedInputRatio: number;
	thinkingTokensPerTurn: number;
	models: ModelInfo[];
	update: Updater;
}) {
	return (
		<>
			<p className="sub">Roughly: page ≈ 750 tokens · full document ≈ 3,000 tokens (4 pages).</p>
			<fieldset>
				<legend className="sr-only">Output size</legend>
				<div className="rstack">
					{RESPONSE_SIZES.map((os) => (
						<label key={os.id} className={`ropt${outputSize === os.id ? ' on' : ''}`}>
							<input
								type="radio"
								name="outputSize"
								value={os.id}
								checked={outputSize === os.id}
								onChange={() => update({ outputSize: os.id })}
								className="ropt-input"
							/>
							<div className="rdot" aria-hidden="true" />
							<div>
								<span className="rtxt">{os.label}</span>
								<span className="rdesc">{os.desc}</span>
							</div>
						</label>
					))}
				</div>
			</fieldset>
			<label
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					gap: 10,
					marginTop: 10,
				}}
			>
				<span className="flbl" style={{ marginBottom: 0 }}>
					How many?
				</span>
				<input
					type="number"
					min={1}
					max={1000000}
					value={outputSizeCount}
					className="num"
					style={{ width: 100 }}
					onChange={(e) => update({ outputSizeCount: Math.max(1, Math.trunc(Number(e.target.value) || 1)) })}
				/>
			</label>
			{expert && (
				<>
					<label className="flbl" style={{ marginTop: 14 }}>
						Model
						<select
							style={{ display: 'block', marginTop: 4, fontSize: 13, padding: '4px 6px' }}
							value={modelId ?? ''}
							onChange={(e) => update({ modelId: e.target.value })}
						>
							{models.map((m) => (
								<option key={m.modelId} value={m.modelId}>
									{m.displayName}
								</option>
							))}
						</select>
					</label>
					<label className="flbl" style={{ marginTop: 10 }}>
						Pricing tier
						<select
							style={{ display: 'block', marginTop: 4, fontSize: 13, padding: '4px 6px' }}
							value={pricingTier}
							onChange={(e) => update({ pricingTier: e.target.value as WizardState['pricingTier'] })}
						>
							{PRICING_TIERS.map((t) => (
								<option key={t.id} value={t.id}>
									{t.label}
								</option>
							))}
						</select>
					</label>
					<label className="flbl" style={{ marginTop: 10 }}>
						Context caching: {Math.round(cachedInputRatio * 100)}% of input tokens
						<input
							type="range"
							min={0}
							max={100}
							value={Math.round(cachedInputRatio * 100)}
							onChange={(e) => update({ cachedInputRatio: Number(e.target.value) / 100 })}
						/>
					</label>
					<div style={{ marginTop: 10 }}>
						<label
							className="flbl"
							htmlFor="thinking-tokens"
							style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}
						>
							<span>Extended "thinking" tokens per response</span>
							<input
								id="thinking-tokens"
								type="number"
								min={0}
								max={1000000}
								value={thinkingTokensPerTurn}
								className="num"
								style={{ width: 100 }}
								onChange={(e) =>
									update({ thinkingTokensPerTurn: Math.max(0, Math.trunc(Number(e.target.value) || 0)) })
								}
							/>
						</label>
						<div className="note">
							Extra tokens a model generates to reason internally before it answers (e.g. Gemini's thinking / reasoning
							steps). They're billed as output tokens and added to every response. "Per response" means one input
							processed — a single chat turn, or one item in a batch. Leave at 0 if the model isn't using extended
							thinking.
						</div>
					</div>
				</>
			)}
		</>
	);
}
